import React, { useEffect, useState } from 'react';
import { HiOutlineCog, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import communicationApi from '../../../../../../services/communicationApi';
import { EmailChannelConfig, EmailEnvelope, Template } from '../../../../../../types/communication';
import SectionCard from '../../shared/SectionCard';
import ComboTextField from '../../shared/ComboTextField';
import VariableTextField from '../../shared/VariableTextField';
import EmailEnvelopeEditor from './EmailEnvelopeEditor';

interface EmailDeliveryProps {
    config: EmailChannelConfig;
    envelopeTemplate: Template | null;
    disabled: boolean;
    onConfigChange: (config: EmailChannelConfig) => void;
    onEnvelopeChange: (envelope: EmailEnvelope) => void;
    onCreateEnvelope: () => void;
}

const EmailDelivery: React.FC<EmailDeliveryProps> = ({
    config,
    envelopeTemplate,
    disabled,
    onConfigChange,
    onEnvelopeChange,
    onCreateEnvelope,
}) => {
    const { adapter } = config;
    const setAdapter = (patch: Partial<EmailChannelConfig['adapter']>) => onConfigChange({ ...config, adapter: { ...adapter, ...patch } });
    const envelope = envelopeTemplate?.envelope;
    const variables = envelopeTemplate?.variables ?? [];

    const [sesTemplates, setSesTemplates] = useState<string[]>([]);
    const [configurationSets, setConfigurationSets] = useState<string[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setCatalogLoading(true);
        Promise.all([communicationApi.getSesTemplates(), communicationApi.getSesConfigurationSets()])
            .then(([templates, sets]) => {
                if (!active) return;
                setSesTemplates(templates.map((t) => t.name));
                setConfigurationSets(sets.map((s) => s.name));
            })
            .catch(() => undefined)
            .finally(() => {
                if (active) setCatalogLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    return (
        <div className="flex flex-col gap-4">
            {!envelope ? (
                <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 p-8 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Esta composição ainda não tem o modelo de envio (assunto, destinatários e arquivos).</p>
                    {!disabled && (
                        <button type="button" onClick={onCreateEnvelope} className="mt-3 text-sm text-teal-700 dark:text-teal-300 hover:underline">
                            Criar modelo de envio
                        </button>
                    )}
                </div>
            ) : (
                <EmailEnvelopeEditor envelope={envelope} variables={variables} disabled={disabled} onChange={onEnvelopeChange} />
            )}

            <SectionCard
                title="Configurações técnicas de entrega (SES)"
                description="Já vem pronto. Só mexa se o time de engenharia pedir."
                icon={<HiOutlineCog size={18} />}
                tone="muted"
                collapsible
                defaultOpen={false}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ComboTextField
                        id="ses-template"
                        label="Nome do modelo no SES"
                        value={adapter.providerTemplateName}
                        options={sesTemplates}
                        disabled={disabled}
                        loading={catalogLoading}
                        hint="Escolha um modelo já registrado no SES ou digite o nome de um novo."
                        onChange={(providerTemplateName) => setAdapter({ providerTemplateName })}
                    />
                    <ComboTextField
                        id="ses-configuration-set"
                        label="Configuration set (métricas)"
                        value={adapter.configurationSet}
                        options={configurationSets}
                        disabled={disabled}
                        loading={catalogLoading}
                        hint="Conjunto de métricas do SES. Deixe em branco se não usar."
                        onChange={(configurationSet) => setAdapter({ configurationSet })}
                    />
                </div>
                <div className="mt-4">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Marcadores (tags) para relatórios</div>
                    <div className="flex flex-col gap-2">
                        {adapter.tags.map((tag, index) => (
                            <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center">
                                <VariableTextField
                                    id={`tag-name-${index}`}
                                    label="Nome"
                                    value={tag.name}
                                    disabled={disabled}
                                    onChange={(name) => setAdapter({ tags: adapter.tags.map((t, i) => (i === index ? { ...t, name } : t)) })}
                                />
                                <VariableTextField
                                    id={`tag-value-${index}`}
                                    label="Valor"
                                    value={tag.value}
                                    disabled={disabled}
                                    onChange={(value) => setAdapter({ tags: adapter.tags.map((t, i) => (i === index ? { ...t, value } : t)) })}
                                />
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => setAdapter({ tags: adapter.tags.filter((_, i) => i !== index) })}
                                        className="text-gray-400 hover:text-red-600"
                                        aria-label="Remover marcador"
                                    >
                                        <HiOutlineTrash size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => setAdapter({ tags: [...adapter.tags, { name: '', value: '' }] })}
                                className="self-start inline-flex items-center gap-1 text-xs text-teal-700 dark:text-teal-300 hover:underline"
                            >
                                <HiOutlinePlus size={14} /> Adicionar marcador
                            </button>
                        )}
                        {disabled && !adapter.tags.length && <span className="text-sm text-gray-400">Nenhum</span>}
                    </div>
                </div>
            </SectionCard>
        </div>
    );
};

export default EmailDelivery;
